import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Plus, Sprout, Calendar, X, DollarSign, Sparkles, Share2, AlertTriangle, Download, Lock } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { MarkFailedModal, MicroLessonToast } from '@/components/DailyEngagement';
import { HarvestPhotoPicker } from '@/components/PhotoUpload';
import { useWeatherPredictions, WeatherEstimateLine } from '@/components/WeatherForecast';
import { loadWithCache } from '@/lib/offlineCache';
import { OfflineBanner } from '@/components/OfflineBanner';
import { cropImage } from '@/lib/cropImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MyCrops({ user }) {
  const [plantings, setPlantings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [harvestModal, setHarvestModal] = useState(null); // active planting
  const [celebration, setCelebration] = useState(null);
  const [failedModal, setFailedModal] = useState(null);
  const [showLessonToast, setShowLessonToast] = useState(false);
  const [envFilter, setEnvFilter] = useState('all');
  const weather = useWeatherPredictions();
  const wmap = {};
  (weather?.predictions || []).forEach(r => { wmap[r.planting_id] = r; });

  const removeCrop = async (p) => {
    if (!window.confirm(`Remove ${p.crop_name} from your garden? This deletes it and won't log a harvest.`)) return;
    try {
      await axios.delete(`${API}/plantings/${p.planting_id}`, { withCredentials: true });
      fetchPlantings();
    } catch (e) { console.error(e); }
  };

  const fetchPlantings = async () => {
    try {
      const { data, offline: off, savedAt: sa } = await loadWithCache('my_crops', async () => {
        const response = await axios.get(`${API}/plantings`, { withCredentials: true });
        return Array.isArray(response.data) ? response.data : [];
      });
      setPlantings(data.filter(p => p.stage !== 'finished'));
      setOffline(off);
      setSavedAt(sa);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPlantings(); }, []);
  useEffect(() => {
    const onOnline = () => fetchPlantings();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  const getStageColor = (stage) => {
    const colors = {
      seed: 'bg-[#C9A85B]', germination: 'bg-[#B4D330]', seedling: 'bg-[#8EAA97]',
      transplant: 'bg-[#1A4331]', growth: 'bg-[#1A4331]', flowering: 'bg-[#C66B4A]', harvest: 'bg-[#C9A85B]'
    };
    return colors[stage] || 'bg-[#8EAA97]';
  };

  const formatDate = (s) => s ? new Date(s).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set';

  const ENV_META = { outdoor: 'Garden', greenhouse: 'Greenhouse', indoor: 'Indoors' };
  const shown = envFilter === 'all' ? plantings : plantings.filter((p) => (p.environment || 'outdoor') === envFilter);

  const onHarvestLogged = (data) => {
    setCelebration(data._celebration);
    setHarvestModal(null);
    fetchPlantings();
  };

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>My Crops</h1>
            <p className="text-base text-[#7A6B57] mt-2">Track your vegetables from seed to harvest</p>
          </div>
          <Link to="/crops" data-testid="add-crop-button" className="bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] font-semibold py-3 px-6 rounded-full transition-colors flex items-center gap-2">
            <Plus className="w-5 h-5" /> Add Crop
          </Link>
        </div>

        {offline && <OfflineBanner savedAt={savedAt} onRetry={fetchPlantings} />}

        {loading ? (
          <div className="flex items-center justify-center h-96"><div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div></div>
        ) : plantings.length === 0 ? (
          <div className="bg-[#FAF6EC] rounded-3xl p-12 border border-[#1A4331]/10 text-center">
            <Sprout className="w-16 h-16 text-[#8EAA97] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#1A4331] mb-2">No crops yet</h3>
            <p className="text-[#7A6B57] mb-6">Start tracking your vegetables by adding your first crop</p>
            <Link to="/crops" data-testid="browse-crops-link" className="inline-block bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] font-semibold py-3 px-8 rounded-full transition-colors">Browse Crop Library</Link>
          </div>
        ) : (
          <>
          <HarvestForecast />
          <div className="flex flex-wrap gap-2" data-testid="env-filter">
            {[
              { key: 'all', label: 'All' },
              { key: 'outdoor', label: 'Garden' },
              { key: 'greenhouse', label: 'Greenhouse' },
              { key: 'indoor', label: 'Indoors' },
            ].map((f) => (
              <button key={f.key} onClick={() => setEnvFilter(f.key)} data-testid={`env-filter-${f.key}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors ${envFilter === f.key ? 'bg-[#1A4331] text-white border-[#1A4331]' : 'bg-white text-[#1A4331] border-[#1A4331]/15 hover:border-[#1A4331]/40'}`}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {shown.map((p) => (
              <div key={p.planting_id} data-testid={`planting-card-${p.planting_id}`} className="bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10 card-hover">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-[#1A4331]/10 bg-[#E5EBDD]">
                      <img src={cropImage(p.crop_name, wmap[p.planting_id]?.category)} alt={p.crop_name} loading="lazy" className="w-full h-full object-cover" data-testid={`crop-photo-${p.planting_id}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.crop_name}</h3>
                      {p.variety && <p className="text-sm text-[#7A6B57]">{p.variety}</p>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${getStageColor(p.stage)}`}>{p.stage.replace('_', ' ')}</span>
                    <span data-testid={`env-badge-${p.planting_id}`} className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#1A4331]/8 text-[#1A4331]">{ENV_META[p.environment || 'outdoor']}</span>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-[#7A6B57]">
                  <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Sowed: {formatDate(p.sowing_date)}</div>
                  {(() => {
                    const hw = wmap[p.planting_id]?.harvest_window;
                    if (hw) return (<div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Harvest window: {formatDate(hw.earliest)} – {formatDate(hw.latest)}{p.expected_harvest_date ? <span className="text-xs text-[#7A6B57]/70">· ~{formatDate(p.expected_harvest_date)}</span> : null}</div>);
                    return p.expected_harvest_date ? (<div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Expected harvest: {formatDate(p.expected_harvest_date)}</div>) : null;
                  })()}
                  {p.plant_count && <div>{p.plant_count} plants</div>}
                </div>
                {wmap[p.planting_id] && <WeatherEstimateLine report={wmap[p.planting_id]} />}
                <div className="mt-4 pt-4 border-t border-[#1A4331]/10 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-[#E5EBDD] rounded-full overflow-hidden">
                    <div className={`h-full ${getStageColor(p.stage)} transition-all duration-500`} style={{ width: `${{seed:14,germination:28,seedling:42,transplant:56,growth:70,flowering:85,harvest:100,failed:100}[p.stage] || 0}%` }}></div>
                  </div>
                  {p.stage !== 'failed' && p.stage !== 'harvest' && (
                    <button onClick={() => setFailedModal(p)} data-testid={`mark-failed-${p.planting_id}`} title="Mark as failed" className="text-xs text-[#C66B4A] hover:bg-[#C66B4A]/10 font-semibold py-2 px-3 rounded-full whitespace-nowrap">
                      <AlertTriangle className="w-3.5 h-3.5 inline" />
                    </button>
                  )}
                  <button onClick={() => removeCrop(p)} data-testid={`remove-crop-${p.planting_id}`} title="Remove from garden" className="text-xs text-[#7A6B57] border border-[#1A4331]/20 hover:border-[#C66B4A] hover:text-[#C66B4A] font-semibold py-2 px-3 rounded-full whitespace-nowrap">
                    Remove
                  </button>
                  <button onClick={() => setHarvestModal(p)} data-testid={`harvest-crop-${p.planting_id}`} title="Harvest & archive to your record" className="bg-[#C9A85B] hover:bg-[#B0934E] text-white font-semibold text-xs py-2 px-4 rounded-full whitespace-nowrap">Harvest</button>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>

      {harvestModal && <HarvestLogModal planting={harvestModal} onClose={() => setHarvestModal(null)} onLogged={onHarvestLogged} />}
      {celebration && <HarvestCelebration data={celebration} onClose={() => { setCelebration(null); setShowLessonToast(true); }} user={user} />}
      <MarkFailedModal open={!!failedModal} planting={failedModal} onClose={() => setFailedModal(null)} onMarked={fetchPlantings} />
      {showLessonToast && <MicroLessonToast context="harvest" onClose={() => setShowLessonToast(false)} />}
    </Layout>
  );
}

function HarvestForecastLocked() {
  return (
    <div data-testid="harvest-forecast-locked" className="relative overflow-hidden rounded-3xl p-6 bg-gradient-to-br from-[#1A4331] to-[#0F2D1F] text-[#FAF6EC]">
      <div className="absolute -top-16 -right-10 w-56 h-56 bg-[#B4D330]/15 rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-[#B4D330]">Harvest Forecast</p>
        <div className="flex items-start gap-3 mt-2">
          <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#15351F', border: '1px solid rgba(231,182,75,0.6)' }}>
            <Lock className="w-5 h-5" style={{ color: '#E7B64B' }} />
          </div>
          <div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>See your harvest forecast</h2>
            <p className="text-sm text-[#FAF6EC]/75 mt-1 max-w-md">Weather-driven harvest dates and the value of everything you grow — unlock with Homegrown Premium.</p>
          </div>
        </div>
        <Link to="/paywall" data-testid="harvest-forecast-unlock" className="hg-press mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-bold transition-all" style={{ background: '#B4D330', color: '#0F2D1F' }}>
          <Sparkles className="w-4 h-4" /> Unlock with Premium
        </Link>
      </div>
    </div>
  );
}

function HarvestForecast() {
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    axios.get(`${API}/harvests/forecast`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch((err) => { if (err?.response?.status === 402) setLocked(true); });
  }, []);
  if (locked) return <HarvestForecastLocked />;
  if (!data) return null;
  const perf = data.performance || {};
  const up = data.upcoming || {};
  const counts = [
    { key: 'ready', label: 'Ready now', n: (up.ready || []).length, tone: '#6B9F4A' },
    { key: 'this_week', label: 'This week', n: (up.this_week || []).length, tone: '#C9A85B' },
    { key: 'this_month', label: 'This month', n: (up.this_month || []).length, tone: '#8FB4D6' },
  ];
  const soon = [...(up.ready || []), ...(up.this_week || []), ...(up.this_month || []), ...(up.later || [])].slice(0, 6);
  const fmt = (s) => s ? new Date(s).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' }) : '';
  const seasonMid = perf.season_value && perf.season_value.high > 0 ? Math.round((perf.season_value.low + perf.season_value.high) / 2) : 0;
  return (
    <div data-testid="harvest-forecast" className="relative overflow-hidden rounded-3xl p-6 bg-gradient-to-br from-[#1A4331] to-[#0F2D1F] text-[#FAF6EC]">
      <div className="absolute -top-16 -right-10 w-56 h-56 bg-[#B4D330]/15 rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-[#B4D330]">Harvest Forecast</p>
        <h2 className="text-2xl sm:text-3xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="forecast-headline">{perf.headline}</h2>
        {perf.total_harvests > 0 && (
          <p className="text-sm text-[#FAF6EC]/70 mt-1">{perf.total_harvests} harvests logged · {perf.total_kg}kg grown{seasonMid > 0 ? ` · $${seasonMid} this season` : ''}</p>
        )}

        <div className="flex flex-wrap gap-2 mt-4">
          {counts.map(c => (
            <div key={c.key} data-testid={`forecast-count-${c.key}`} className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: c.tone }} />
              <span className="text-sm font-semibold">{c.n}</span>
              <span className="text-xs text-[#FAF6EC]/70">{c.label}</span>
            </div>
          ))}
        </div>

        {data.available === false && data.reason === 'no_location' ? (
          <p className="text-sm text-[#FAF6EC]/70 mt-4">Set your garden location to forecast harvest dates.</p>
        ) : soon.length === 0 ? (
          <p className="text-sm text-[#FAF6EC]/70 mt-4">No harvests on the horizon yet — add a crop to start your forecast.</p>
        ) : (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="forecast-upcoming">
            {soon.map(it => (
              <Link key={it.planting_id} to={`/crops/${it.crop_id}`} data-testid={`forecast-item-${it.planting_id}`} className="flex items-center gap-2 bg-white/10 hover:bg-white/15 rounded-2xl p-2 transition-colors">
                <img src={cropImage(it.crop_name, it.category)} alt={it.crop_name} loading="lazy" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{it.crop_name}</p>
                  <p className="text-xs text-[#B4D330]">{it.ready || it.days_remaining <= 0 ? 'Ready now' : fmt(it.predicted_date)}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HarvestLogModal({ planting, onClose, onLogged }) {
  const [weight, setWeight] = useState('');
  const [count, setCount] = useState('');
  const [notes, setNotes] = useState('');
  const [keepGrowing, setKeepGrowing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/harvests`, {
        planting_id: planting.planting_id,
        crop_id: planting.crop_id,
        crop_name: planting.crop_name,
        weight_kg: parseFloat(weight) || 0,
        quantity: count ? parseInt(count, 10) : undefined,
        notes,
      }, { withCredentials: true });
      // Harvest archives the crop out of the active garden unless it's still producing
      if (!keepGrowing) {
        await axios.patch(`${API}/plantings/${planting.planting_id}`, { stage: 'finished', actual_harvest_date: new Date().toISOString() }, { withCredentials: true }).catch(() => {});
      }
      onLogged(res.data);
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAF6EC] rounded-3xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="harvest-log-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Harvest {planting.crop_name}</h3>
          <button onClick={onClose} className="text-[#7A6B57]"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#7A6B57] mb-4">How much did you pick? We'll add it to your harvest record.</p>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-[#1A4331] mb-1">Weight (kg)</label>
              <input type="number" step="0.1" min="0" required value={weight} onChange={e => setWeight(e.target.value)} data-testid="harvest-weight-input" placeholder="e.g. 1.4" className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1A4331] mb-1">Count (optional)</label>
              <input type="number" step="1" min="0" value={count} onChange={e => setCount(e.target.value)} data-testid="harvest-count-input" placeholder="e.g. 6" className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#1A4331] mb-1">Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#7A6B57] cursor-pointer select-none" data-testid="harvest-keep-growing">
            <input type="checkbox" checked={keepGrowing} onChange={e => setKeepGrowing(e.target.checked)} className="w-4 h-4 accent-[#1A4331]" />
            Still producing — keep it growing in my garden
          </label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border-2 border-[#8EAA97] text-[#1A4331] py-3 rounded-full font-semibold">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="harvest-submit-button" className="flex-1 bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] py-3 rounded-full font-semibold disabled:opacity-50">{submitting ? 'Saving...' : (keepGrowing ? 'Log harvest' : 'Harvest it')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function HarvestCelebration({ data, onClose, user }) {
  const [shareOpen, setShareOpen] = useState(false);
  const [showDownloadCard, setShowDownloadCard] = useState(false);
  const firstName = user?.name?.split(' ')[0] || 'Gardener';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gradient-to-br from-[#FAF6EC] to-[#E5EBDD] rounded-3xl p-8 w-full max-w-md relative overflow-hidden" onClick={e => e.stopPropagation()} data-testid="harvest-celebration">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#7A6B57]"><X className="w-5 h-5" /></button>
        <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#C9A85B]/15 rounded-full blur-3xl"></div>
        <div className="relative text-center">
          <div className="inline-block animate-pulse">
            <Sparkles className="w-10 h-10 text-[#C9A85B] mx-auto mb-2" />
          </div>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#C9A85B]">Nice work, {firstName}</p>
          <h2 className="text-3xl font-bold text-[#1A4331] mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Beautiful harvest</h2>
          <p className="text-sm text-[#7A6B57] mt-1">You picked {data.weight_kg}kg of {data.crop_name}</p>

          <div className="my-6 py-5 px-4 bg-white/60 rounded-2xl">
            <p className="text-xs font-bold tracking-wider uppercase text-[#7A6B57]">Estimated value</p>
            <p className="text-4xl font-bold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ${data.value_low}–${data.value_high}
            </p>
            <p className="text-xs text-[#7A6B57] mt-2">+{data.xp_awarded} XP earned</p>
          </div>

          <div className="text-left bg-white/40 rounded-2xl p-4 mb-4">
            <p className="text-xs font-bold tracking-wider uppercase text-[#7A6B57]">This season</p>
            <p className="text-2xl font-bold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ${data.season_total_value_low}–${data.season_total_value_high} grown
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-4">
            <button 
              onClick={() => setShareOpen(true)} 
              data-testid="share-harvest-button" 
              className="bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] py-3 rounded-full font-semibold flex items-center justify-center gap-2"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button 
              onClick={() => setShowDownloadCard(true)} 
              data-testid="download-card-button" 
              className="bg-[#6B9F4A] hover:bg-[#5A8539] text-white py-3 rounded-full font-semibold text-sm"
            >
              Download card
            </button>
          </div>
          <button onClick={onClose} data-testid="celebration-close" className="w-full mt-2 border-2 border-[#1A4331] text-[#1A4331] py-3 rounded-full font-semibold">Done</button>
        </div>
      </div>
      {shareOpen && <ShareToCommunity data={data} user={user} onClose={() => { setShareOpen(false); onClose(); }} />}
      {showDownloadCard && <HarvestCardDownload data={data} user={user} onClose={() => setShowDownloadCard(false)} />}
    </div>
  );
}

function ShareToCommunity({ data, user, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [shareMode, setShareMode] = useState('region');
  const [hideValue, setHideValue] = useState(false);
  const [note, setNote] = useState('');
  const [photoUrl, setPhotoUrl] = useState(null);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/community/posts`, {
        crop_name: data.crop_name,
        harvest_quantity: data.weight_kg,
        harvest_unit: 'kg',
        estimated_value_low: hideValue ? null : data.value_low,
        estimated_value_high: hideValue ? null : data.value_high,
        note,
        photo_urls: photoUrl ? [photoUrl] : [],
        visibility: shareMode === 'anonymous' ? 'public' : (shareMode === 'public' ? 'public' : 'region'),
        display_name: shareMode === 'anonymous' ? 'Anonymous gardener' : (user?.name?.split(' ')[0] || 'Gardener'),
        growing_style: user?.growing_preference || 'mixed',
      }, { withCredentials: true });
      onClose();
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAF6EC] rounded-3xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="share-celebration-modal">
        <h3 className="text-2xl font-bold text-[#1A4331] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Share with community</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-semibold text-[#1A4331] mb-2">How?</label>
            <select value={shareMode} onChange={e => setShareMode(e.target.value)} className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10">
              <option value="region">Share with region only</option>
              <option value="anonymous">Share anonymously</option>
              <option value="public">Public (full profile)</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#1A4331] cursor-pointer">
            <input type="checkbox" checked={hideValue} onChange={e => setHideValue(e.target.checked)} /> Hide harvest value
          </label>
          <textarea placeholder="A quick tip or note (optional)" value={note} onChange={e => setNote(e.target.value)} rows={2} className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10" />
          <HarvestPhotoPicker photoUrl={photoUrl} onChange={setPhotoUrl} />
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 border-2 border-[#8EAA97] text-[#1A4331] py-3 rounded-full font-semibold">Skip</button>
            <button onClick={submit} disabled={submitting} data-testid="confirm-share-button" className="flex-1 bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] py-3 rounded-full font-semibold disabled:opacity-50">{submitting ? 'Sharing...' : 'Share'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Harvest Card Download Component
function HarvestCardDownload({ data, user, onClose }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardRef = React.useRef(null);
  
  const firstName = user?.name?.split(' ')[0] || 'Gardener';

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const res = await axios.post(`${API}/harvests/share-card`, {
          crop_name: data.crop_name,
          weight_kg: data.weight_kg,
          value_low: data.value_low,
          value_high: data.value_high,
          xp_earned: data.xp_awarded,
          days_from_seed: data.days_from_sowing
        }, { withCredentials: true });
        setCard(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyToClipboard = () => {
    if (card?.card_text) {
      navigator.clipboard.writeText(card.card_text);
      alert('Harvest card copied to clipboard! 📋');
    }
  };

  const downloadAsImage = () => {
    // Future enhancement: use html2canvas or similar to generate image
    alert('Image download coming soon! For now, use the copy button to share text.');
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="harvest-card-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-[#1A4331]">Your Harvest Card</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#5C665C]" /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Visual Card */}
            <div 
              ref={cardRef}
              className="bg-gradient-to-br from-[#6B9F4A] to-[#1A4331] rounded-2xl p-8 text-white mb-4"
              data-testid="harvest-card-visual"
            >
              <div className="text-center">
                <div className="text-4xl mb-2">🌱</div>
                <p className="text-xs font-bold tracking-wider uppercase opacity-80">HARVEST CELEBRATION</p>
                <h2 className="text-2xl font-bold mt-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {firstName} harvested
                </h2>
                <p className="text-4xl font-bold my-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {data.weight_kg}kg
                </p>
                <p className="text-xl font-semibold mb-4">of {data.crop_name}</p>
                
                <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
                  <p className="text-sm opacity-90">💰 Value: ${data.value_low}-${data.value_high}</p>
                  {data.days_from_sowing && (
                    <p className="text-sm opacity-90 mt-1">🎯 {data.days_from_sowing} days from seed</p>
                  )}
                  <p className="text-sm opacity-90 mt-1">⭐ +{data.xp_awarded} XP</p>
                </div>
                
                <p className="text-xs mt-4 opacity-75">Grown with Homegrown 🌿</p>
              </div>
            </div>

            {/* Text Version */}
            <div className="bg-[#F0F2EB] rounded-2xl p-4 mb-4">
              <p className="text-xs font-bold text-[#8EAA97] mb-2">Text version:</p>
              <pre className="text-sm text-[#2D241E] whitespace-pre-wrap font-mono">
                {card?.card_text}
              </pre>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={copyToClipboard}
                className="bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 rounded-full transition-colors"
                data-testid="copy-card-btn"
              >
                Copy text
              </button>
              <button
                onClick={downloadAsImage}
                className="border-2 border-[#1A4331] text-[#1A4331] hover:bg-[#1A4331]/5 font-semibold py-3 rounded-full transition-colors"
                data-testid="download-image-btn"
              >
                Download image
              </button>
            </div>
            <p className="text-xs text-[#8EAA97] text-center mt-3">
              Share this on social media to inspire other gardeners! 🌱
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default MyCrops;
